/**
 * Juntar as duas metades do que o app sabe sobre uma criação.
 *
 * A API responde em duas rotas porque são duas perguntas diferentes, com
 * fontes diferentes e lacunas diferentes: `/v1/rebanho` diz **quanto** de
 * dejeto cada animal gera por dia, `/v1/substratos` diz **o que** esse dejeto
 * é (sólidos, potencial de metano). As duas faltam de forma independente —
 * bovino a pasto tem análise do esterco e ninguém mediu quanto se consegue
 * recolher do pasto; leitão de creche tem a geração medida e ninguém analisou
 * o material.
 *
 * A tela precisa das duas juntas antes de mostrar o formulário, por um motivo
 * concreto: é isso que permite dizer "falta tal análise" **antes** do produtor
 * digitar e apertar calcular. Um 422 depois do envio tem a mesma informação e
 * chega tarde demais — ele já esperou, já criou expectativa, e a recusa parece
 * defeito do app em vez de lacuna conhecida do setor.
 *
 * Cama de aviário é o caso que obriga este módulo a existir: ela tem análise
 * (88,28 Nm³ CH4/t) e **não** tem geração por cabeça, porque cama se mede por
 * lote e por metro quadrado, nunca por frango. Oferecer "quantos frangos você
 * tem?" ali é garantir uma recusa.
 */

/** Como se chama o grupo na tela. A API devolve a espécie em minúscula. */
export const NOME_DA_ESPECIE = {
  'suíno': 'Suínos',
  suino: 'Suínos',
  bovino: 'Bovinos',
  ave: 'Aves',
};

/** Ordem dos grupos: a maior base instalada de biodigestor no Brasil primeiro. */
const ORDEM_DAS_ESPECIES = ['suíno', 'suino', 'bovino', 'ave'];

function posicao(especie) {
  const i = ORDEM_DAS_ESPECIES.indexOf(especie);
  return i === -1 ? ORDEM_DAS_ESPECIES.length : i;
}

/**
 * Uma linha por categoria, com as duas metades e o veredito de cada escala.
 *
 * `rebanho` e `catalogo` são as listas `itens` das duas rotas. A junção é pela
 * chave; uma categoria que exista só de um lado entra mesmo assim, com a outra
 * metade vazia — sumir com ela esconderia justamente a lacuna.
 */
export function juntarEspecies(rebanho = [], catalogo = []) {
  const porChave = new Map();

  for (const g of rebanho) {
    porChave.set(g.chave, { rebanho: g, substrato: null });
  }
  for (const s of catalogo) {
    const atual = porChave.get(s.chave);
    if (atual) atual.substrato = s;
    else porChave.set(s.chave, { rebanho: null, substrato: s });
  }

  const itens = [];
  for (const [chave, { rebanho: g, substrato: s }] of porChave) {
    const dejetoCabDia = g?.valor ?? s?.dejeto_kg_cab_dia ?? null;
    const temAnalise = (s?.rendimento_nm3_t ?? null) !== null;

    itens.push({
      chave,
      // O rótulo do animal vem do rebanho; o do substrato descreve o material.
      rotulo: g?.rotulo ?? s?.rotulo ?? chave,
      rotuloDoMaterial: s?.rotulo ?? '',
      especie: g?.especie ?? s?.especie ?? '',
      forma: g?.forma ?? s?.forma ?? '',

      dejetoCabDia,
      faixaDoDejeto: g?.faixa ?? null,
      variacaoNaFaixaPct: g?.variacao_na_faixa_pct ?? null,
      incluiProgenie: g?.inclui_progenie === true,
      stTipicoPct: g?.st_tipico_pct ?? null,

      temAnalise,
      rendimentoNm3T: s?.rendimento_nm3_t ?? null,
      metanoPorCabecaDia: s?.nm3_ch4_por_cabeca_dia ?? null,
      perdaSeEstocarPct: s?.perda_se_estocar_pct ?? null,
      confianca: s?.confianca ?? '',

      // A pendência pode vir dos dois lados, e são textos diferentes.
      pendenciaDaAnalise: s?.pendencia ?? '',
      pendenciaDaGeracao: g?.pendencia ?? '',
      analogo: s?.analogo ?? '',

      podePorCabeca: dejetoCabDia !== null,
      podeCalcular: temAnalise,
    });
  }

  itens.sort((a, b) => {
    const d = posicao(a.especie) - posicao(b.especie);
    return d !== 0 ? d : a.rotulo.localeCompare(b.rotulo, 'pt-BR');
  });
  return itens;
}

/** `[[espécie, itens]]` na ordem dos grupos, para virar `<optgroup>`. */
export function agruparPorEspecie(itens) {
  const grupos = new Map();
  for (const i of itens) {
    const e = i.especie || 'outros';
    if (!grupos.has(e)) grupos.set(e, []);
    grupos.get(e).push(i);
  }
  return [...grupos.entries()];
}

/**
 * O que impede o cálculo desta categoria, na escala escolhida — em frases.
 *
 * Devolve `[]` quando dá para calcular. Cada frase é dirigida a quem vai
 * resolver a pendência: fala em "mandar analisar" e "pesar o que sai", não em
 * `st_pct` e `bmp`.
 */
export function oQueFalta(item, escala = 'cabecas') {
  if (!item) return [];
  const faltas = [];

  if (escala === 'cabecas' && !item.podePorCabeca) {
    faltas.push(
      item.pendenciaDaGeracao ||
        'ninguém mediu quanto de dejeto se recolhe por animal nesta criação',
    );
  }
  if (!item.temAnalise) {
    faltas.push(
      item.pendenciaDaAnalise ||
        'este material ainda não tem análise de sólidos e de potencial de metano',
    );
  }
  return faltas;
}

/**
 * A escala que a tela de fato usa, dada a que o produtor escolheu antes.
 *
 * Trocar de categoria pode invalidar a escolha: quem vinha de "2000 suinos" e
 * escolhe cama de aviario nao pode continuar em cabecas, porque ninguem
 * publicou quanto de cama um frango gera. A correcao e DERIVADA e nao um
 * `useEffect` que conserta depois -- corrigir no efeito pinta um quadro com a
 * escala errada, e nesse quadro a tela chega a dizer que falta um dado que
 * nao falta.
 *
 * E a funcao que sustenta uma invariante do simulador: categoria que PODE
 * calcular nunca cai numa escala com pendencia. Prova em tres linhas:
 * `podeCalcular` e `temAnalise`; com analise, 'massa' esta em
 * `escalasPossiveis`, logo o retorno aqui e sempre uma escala possivel; e
 * escala possivel com analise nao tem falta nenhuma em `oQueFalta`. O teste
 * desta invariante cobre as quatro combinacoes, que e o espaco inteiro.
 */
export function escalaEscolhida(item, escala) {
  const escalas = escalasPossiveis(item);
  return escalas.includes(escala) ? escala : (escalas[0] ?? escala);
}

/**
 * Quais escalas fazem sentido oferecer para esta categoria.
 *
 * Toneladas por dia vale sempre que houver análise — quem tem balança ou
 * esterqueira medida informa a massa direto, sem passar por cabeça.
 */
export function escalasPossiveis(item) {
  if (!item) return [];
  const escalas = [];
  if (item.podePorCabeca) escalas.push('cabecas');
  if (item.temAnalise) escalas.push('massa');
  return escalas;
}
